import { useEffect, useRef } from 'react';
import { useEditorStore } from '../state/editorStore';
import { Button } from './components/controls';

/**
 * Modal yes/no question raised by a command through `store.askConfirm`.
 * Used for anything destructive or lossy — resizing an oversized image,
 * discarding unsaved work — so the app never takes a silent decision.
 */
export function ConfirmDialog() {
  const confirm = useEditorStore((s) => s.confirm);
  const respond = useEditorStore((s) => s.respondConfirm);
  const confirmButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (confirm) confirmButton.current?.focus();
  }, [confirm]);

  useEffect(() => {
    if (!confirm) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        respond(false);
      }
    };
    // Capture phase so this wins over the global shortcut handler.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [confirm, respond]);

  if (!confirm) return null;

  return (
    <div
      className="fixed inset-0 z-60 flex items-center justify-center p-6"
      data-testid="confirm-dialog"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => respond(false)}
        className="absolute inset-0 cursor-default bg-black/60"
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        className="relative w-full max-w-md rounded-xl border border-line bg-surface p-4 shadow-2xl"
      >
        <h2 id="confirm-title" className="mb-2 text-[14px] font-semibold text-ink">
          {confirm.title}
        </h2>
        <p className="mb-4 whitespace-pre-line text-muted">{confirm.message}</p>
        <div className="flex justify-end gap-2">
          <Button onClick={() => respond(false)}>{confirm.cancelLabel}</Button>
          <Button ref={confirmButton} variant="primary" onClick={() => respond(true)}>
            {confirm.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
